import React from 'react'
import { Box, CircularProgress, IconButton, Menu, Typography, useMediaQuery } from '@mui/material'
import { darken, useTheme } from '@mui/material/styles'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import { fetchMyDuAnVaChiNhanh } from '@/components/common/searchCommon/chiNhanhOptions'
import {
  branchPickerGridSx,
  branchPickerGroupSx,
  branchPickerGroupTitleSx,
  branchPickerItemSx,
  branchPickerListSx,
  branchPickerPaperSx,
  branchPickerThemeColors,
} from '@/components/common/branchPicker/branchPickerPanelStyles'
import { changeBranchSession } from '@/utils/changeBranchSession'
import { useCurrentChiNhanhId } from '@/hooks/useCurrentChiNhanhId'
import { showMessage } from '@/utils/commonUtils'
import { profileStorage } from '@/services/tokenStorage'
import { useI18n } from '@/contexts/I18nContext'
import {
  banhangKiotLike,
  getKiotHeaderNavHighlightSurfaceSx,
  getKiotHeaderNavHoverBackground,
  KIOT_HEADER_NAV_PADDING_X,
  KIOT_HEADER_NAV_PADDING_Y,
} from '@/components/layout/banhangKiotTokens'

type FlatBranchOption = {
  id: number
  duAnId: number
  ma: string
  /** Tên hiển thị chi nhánh (ưu tiên hơn `ma`). */
  ten: string
  duAn: { id: number; ma: string; ten: string }
}

function getBranchDisplayLabel(
  branchNameOrCode: string,
  projectNameOrCode: string,
  fallback: string,
): string {
  const branchLabel = branchNameOrCode.trim()
  const projectLabel = projectNameOrCode.trim()
  if (branchLabel && projectLabel) return `${projectLabel} - ${branchLabel}`
  if (branchLabel) return branchLabel
  if (projectLabel) return projectLabel
  return fallback
}

function parseMyDuAnListResponse(res: unknown): unknown[] {
  if (Array.isArray(res)) return res
  if (res && typeof res === 'object') {
    const r = res as Record<string, unknown>
    if (Array.isArray(r.data)) return r.data
    if (Array.isArray(r.results)) return r.results
  }
  return []
}

function resolveDuAnIdFromChiNhanhRow(c: Record<string, unknown>): number {
  const direct = c.duAnId ?? c.du_an_id
  if (direct != null && String(direct).trim() !== '' && !Number.isNaN(Number(direct))) {
    return Number(direct)
  }
  const nested = c.duAn
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const id = (nested as Record<string, unknown>).id
    if (id != null && !Number.isNaN(Number(id))) return Number(id)
  }
  return NaN
}

function flattenFromDuAnAndChiNhanhLists(duAnList: unknown[], chiNhanhList: unknown[]): FlatBranchOption[] {
  const duAnById = new Map<number, { id: number; ma: string; ten: string }>()
  for (const raw of duAnList) {
    if (!raw || typeof raw !== 'object') continue
    const item = raw as Record<string, unknown>
    const id = Number(item.id)
    if (Number.isNaN(id)) continue
    duAnById.set(id, { id, ma: String(item.ma ?? ''), ten: String(item.ten ?? '') })
  }

  const out: FlatBranchOption[] = []
  for (const raw of chiNhanhList) {
    if (!raw || typeof raw !== 'object') continue
    const c = raw as Record<string, unknown>
    const cnId = Number(c.id)
    if (Number.isNaN(cnId)) continue
    const duAnId = resolveDuAnIdFromChiNhanhRow(c)
    if (Number.isNaN(duAnId)) continue
    const nestedDuAn = c.duAn
    const fromNested =
      nestedDuAn && typeof nestedDuAn === 'object' && !Array.isArray(nestedDuAn)
        ? (nestedDuAn as Record<string, unknown>)
        : undefined
    const duAnMeta =
      duAnById.get(duAnId) ??
      (fromNested
        ? {
            id: duAnId,
            ma: String(fromNested.ma ?? ''),
            ten: String(fromNested.ten ?? ''),
          }
        : { id: duAnId, ma: '', ten: '' })
    out.push({
      id: cnId,
      duAnId,
      ma: String(c.ma ?? ''),
      ten: String(c.ten ?? ''),
      duAn: duAnMeta,
    })
  }
  return out
}

function flattenDuAnChiNhanh(duAnList: unknown[]): FlatBranchOption[] {
  const out: FlatBranchOption[] = []
  for (const raw of duAnList) {
    if (!raw || typeof raw !== 'object') continue
    const item = raw as Record<string, unknown>
    const duAnId = Number(item.id)
    if (Number.isNaN(duAnId)) continue
    const duAnMeta = {
      id: duAnId,
      ma: String(item.ma ?? ''),
      ten: String(item.ten ?? ''),
    }
    const userNhomQuyens = item.userNhomQuyens
    const chiNhanhs = item.chiNhanhs
    if (Array.isArray(userNhomQuyens) && userNhomQuyens.length > 0) {
      for (const unq of userNhomQuyens) {
        if (!unq || typeof unq !== 'object') continue
        const cn = (unq as Record<string, unknown>).chiNhanh
        if (!cn || typeof cn !== 'object') continue
        const c = cn as Record<string, unknown>
        const id = Number(c.id)
        if (Number.isNaN(id)) continue
        out.push({
          id,
          duAnId,
          ma: String(c.ma ?? ''),
          ten: String(c.ten ?? ''),
          duAn: duAnMeta,
        })
      }
    } else if (Array.isArray(chiNhanhs) && chiNhanhs.length > 0) {
      for (const cn of chiNhanhs) {
        if (!cn || typeof cn !== 'object') continue
        const c = cn as Record<string, unknown>
        const id = Number(c.id)
        if (Number.isNaN(id)) continue
        out.push({
          id,
          duAnId,
          ma: String(c.ma ?? ''),
          ten: String(c.ten ?? ''),
          duAn: duAnMeta,
        })
      }
    }
  }
  return out
}

function branchOptionsFromProfile(profile: Record<string, unknown> | null): FlatBranchOption[] {
  if (!profile) return []
  const duAn = profile.duAn as Record<string, unknown> | undefined
  const chiNhanh = profile.chiNhanh as Record<string, unknown> | undefined
  if (!duAn || !chiNhanh) return []
  const duAnId = Number(duAn.id)
  const cnId = Number(chiNhanh.id)
  if (Number.isNaN(duAnId) || Number.isNaN(cnId)) return []
  return [
    {
      id: cnId,
      duAnId,
      ma: String(chiNhanh.ma ?? ''),
      ten: String(chiNhanh.ten ?? ''),
      duAn: {
        id: duAnId,
        ma: String(duAn.ma ?? ''),
        ten: String(duAn.ten ?? ''),
      },
    },
  ]
}

const HeaderBranchSwitcher: React.FC = () => {
  const { t } = useI18n()
  const theme = useTheme()
  const branchColumnWidthPx = banhangKiotLike.kiotHeaderBranchVisualWidthPx
  const branchMenuColors = React.useMemo(
    () =>
      branchPickerThemeColors({
        submenuBg: theme.palette.banhang.kiotSubmenuPanelBg,
        contrastText: theme.palette.primary.contrastText,
        primaryMain: theme.palette.primary.main,
        paperBg: theme.palette.background.paper,
        darken,
      }),
    [
      theme.palette.banhang.kiotSubmenuPanelBg,
      theme.palette.primary.contrastText,
      theme.palette.primary.main,
      theme.palette.background.paper,
    ],
  )
  const branchNavHighlightColors = React.useMemo(
    () => ({
      activeBg: theme.palette.banhang.navItemActiveBg,
      activeBgHover: theme.palette.banhang.navItemActiveBgHover,
    }),
    [theme.palette.banhang.navItemActiveBg, theme.palette.banhang.navItemActiveBgHover],
  )
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  useCurrentChiNhanhId()
  const profile = profileStorage.getProfile()
  const profileRecord = profile as Record<string, unknown> | null
  const isStaffUser = profileRecord?.type !== 'customer'

  const [branchOptions, setBranchOptions] = React.useState<FlatBranchOption[]>([])
  const [branchListLoading, setBranchListLoading] = React.useState(false)
  const [branchMenuAnchorEl, setBranchMenuAnchorEl] = React.useState<HTMLElement | null>(null)
  const [branchSwitching, setBranchSwitching] = React.useState(false)

  const closeBranchMenu = React.useCallback(() => setBranchMenuAnchorEl(null), [])

  React.useEffect(() => {
    if (!isStaffUser) return
    let cancelled = false
    ;(async () => {
      setBranchListLoading(true)
      try {
        const [duAnRes, chiNhanhRes] = await fetchMyDuAnVaChiNhanh()
        if (cancelled) return
        const duAnList = parseMyDuAnListResponse(duAnRes)
        const chiNhanhList = parseMyDuAnListResponse(chiNhanhRes)
        let flat = flattenFromDuAnAndChiNhanhLists(duAnList, chiNhanhList)
        if (flat.length === 0) {
          flat = flattenDuAnChiNhanh(duAnList)
        }
        const p = profileStorage.getProfile() as Record<string, unknown> | null
        setBranchOptions(flat.length > 0 ? flat : branchOptionsFromProfile(p))
      } catch {
        if (!cancelled) {
          const p = profileStorage.getProfile() as Record<string, unknown> | null
          setBranchOptions(branchOptionsFromProfile(p))
        }
      } finally {
        if (!cancelled) setBranchListLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isStaffUser])

  const handleChangeBranch = React.useCallback(
    async (item: FlatBranchOption) => {
      const p = profileStorage.getProfile() as Record<string, unknown> | null
      const curDu = p?.duAn as Record<string, unknown> | undefined
      const curCn = p?.chiNhanh as Record<string, unknown> | undefined
      const curDuId = curDu != null ? Number(curDu.id) : NaN
      const curCnId = curCn != null ? Number(curCn.id) : NaN
      if (item.duAnId === curDuId && item.id === curCnId) {
        closeBranchMenu()
        return
      }
      setBranchSwitching(true)
      closeBranchMenu()
      try {
        const ok = await changeBranchSession(item.duAnId, item.id)
        if (!ok) {
          showMessage('error', t('messages.error'))
          setBranchSwitching(false)
        }
      } catch {
        setBranchSwitching(false)
      }
    },
    [closeBranchMenu, t],
  )

  if (!isStaffUser) return null

  const currentDuAn = profileRecord?.duAn as Record<string, unknown> | undefined
  const currentChiNhanh = profileRecord?.chiNhanh as Record<string, unknown> | undefined
  const branchDisplay = getBranchDisplayLabel(
    String(currentChiNhanh?.ma ?? ''),
    String(currentDuAn?.ma ?? ''),
    t('components.menu.branchProject'),
  )

  if (branchListLoading && branchOptions.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          px: 1.5,
        }}
      >
        <CircularProgress size={20} sx={{ color: banhangKiotLike.topNavText }} />
      </Box>
    )
  }
  if (branchOptions.length === 0) return null

  const open = Boolean(branchMenuAnchorEl)
  const navActiveR = banhangKiotLike.navItemActiveRadiusPx

  const branchGroupOrder: number[] = []
  const branchGroupMap = new Map<number, { duAnId: number; label: string; items: FlatBranchOption[] }>()
  for (const opt of branchOptions) {
    let group = branchGroupMap.get(opt.duAnId)
    if (!group) {
      group = {
        duAnId: opt.duAnId,
        label: (opt.duAn.ten || opt.duAn.ma || '').trim() || '—',
        items: [],
      }
      branchGroupMap.set(opt.duAnId, group)
      branchGroupOrder.push(opt.duAnId)
    }
    group.items.push(opt)
  }
  const branchGroups = branchGroupOrder.map((id) => branchGroupMap.get(id)!)

  const branchMenuEntries = branchGroups.map((group, idx) => (
    <Box key={group.duAnId} sx={branchPickerGroupSx(branchMenuColors, idx === 0)}>
      <Typography component="div" sx={branchPickerGroupTitleSx(branchMenuColors)}>
        {group.label}
      </Typography>
      <Box sx={branchPickerGridSx}>
        {group.items.map((opt) => {
          const isActive =
            opt.id === Number(currentChiNhanh?.id) && opt.duAnId === Number(currentDuAn?.id)
          const name = (opt.ten || opt.ma || '').trim() || group.label
          return (
            <Box
              key={`${opt.duAnId}-${opt.id}`}
              component="button"
              type="button"
              title={name}
              disabled={branchSwitching}
              onClick={() => void handleChangeBranch(opt)}
              sx={branchPickerItemSx('theme', branchMenuColors, isActive, {
                disabled: branchSwitching,
              })}
            >
              {name}
            </Box>
          )
        })}
      </Box>
    </Box>
  ))

  return (
    <>
      {isMdUp ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            height: '100%',
            flexShrink: 0,
            width: 'auto',
            minWidth: 0,
            maxWidth: branchColumnWidthPx,
            boxSizing: 'border-box',
            py: `${banhangKiotLike.kiotHeaderRightItemMarginYPx}px`,
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label={t('components.menu.switchBranch')}
            onClick={(e) => setBranchMenuAnchorEl(e.currentTarget)}
            disabled={branchSwitching}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'stretch',
              height: '100%',
              flex: '0 1 auto',
              minWidth: 0,
              minHeight: 0,
              cursor: branchSwitching ? 'wait' : 'pointer',
              textAlign: 'left',
              px: KIOT_HEADER_NAV_PADDING_X,
              py: KIOT_HEADER_NAV_PADDING_Y,
              m: 0,
              boxShadow: 'none',
              color: banhangKiotLike.topNavText,
              fontFamily: banhangKiotLike.kiotFontStack,
              letterSpacing: '0.01em',
              transition: 'background-color 120ms ease, border-color 120ms ease',
              borderRadius: `${navActiveR}px`,
              ...getKiotHeaderNavHighlightSurfaceSx(true, branchNavHighlightColors),
              '&:hover': {
                bgcolor: getKiotHeaderNavHoverBackground(true, branchNavHighlightColors),
                border: banhangKiotLike.navItemActiveBorder,
                boxShadow: 'none',
              },
              '&:active': { boxShadow: 'none', border: banhangKiotLike.navItemActiveBorder },
              '&:disabled': { opacity: 0.7 },
            }}
          >
            {branchSwitching ? (
              <CircularProgress size={16} sx={{ color: banhangKiotLike.topNavText }} />
            ) : (
              <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'inherit', flexShrink: 0, mr: 0.6 }} />
            )}
            <Typography
              component="span"
              variant="caption"
              sx={{
                flex: '0 1 auto',
                minWidth: 0,
                fontWeight: banhangKiotLike.kiotNavFontWeight,
                fontFamily: banhangKiotLike.kiotFontStack,
                fontSize: `${banhangKiotLike.kiotNavFontSizePx}px`,
                lineHeight: banhangKiotLike.kiotLineHeight,
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {branchDisplay}
            </Typography>
          </Box>
        </Box>
      ) : (
        <IconButton
          color="inherit"
          size="small"
          aria-label={`${t('components.menu.switchBranch')}: ${branchDisplay}`}
          onClick={(e) => setBranchMenuAnchorEl(e.currentTarget)}
          disabled={branchSwitching}
          sx={{
            color: '#fff',
            alignSelf: 'stretch',
            height: '100%',
            width: 40,
            minWidth: 40,
            borderRadius: `${navActiveR}px`,
            py: 0,
            transition: 'background-color 120ms ease',
            bgcolor: branchNavHighlightColors.activeBg,
            '&:hover': {
              bgcolor: branchNavHighlightColors.activeBgHover,
            },
          }}
        >
          {branchSwitching ? (
            <CircularProgress size={16} sx={{ color: banhangKiotLike.topNavText }} />
          ) : (
            <BusinessOutlinedIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      )}
      <Menu
        anchorEl={branchMenuAnchorEl}
        open={open}
        onClose={closeBranchMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: branchPickerPaperSx('theme', branchMenuColors) },
          list: { sx: branchPickerListSx },
        }}
      >
        {branchMenuEntries}
      </Menu>
    </>
  )
}

export default HeaderBranchSwitcher
